import './lib/sentry'
import { StrictMode } from 'react'
import Clarity from '@microsoft/clarity'
import ReactDOM from 'react-dom/client'
import { AxiosError } from 'axios'
import {
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/auth-store'
import { handleServerError } from '@/lib/handle-server-error'
import { isStaleChunkError, reloadForStaleChunk } from '@/lib/stale-chunk'
import { AuthProvider } from '@/components/auth-provider'
import { TenantProvider } from './context/tenant-context'
import { DirectionProvider } from './context/direction-provider'
import { FontProvider } from './context/font-provider'
import { ThemeProvider } from './context/theme-provider'
import * as Sentry from '@sentry/react'
import { iniciarObservabilidadeEnvio } from '@/lib/sentry-envio'
import { SentryErrorBoundary } from '@/components/sentry-error-boundary'
import { EnvioColaboradorPage } from '@/features/envio-colaborador'
import { ehHostDoEnvio, slugDoCaminho } from '@/lib/endereco-envio'
// Generated Routes
import { routeTree } from './routeTree.gen'
// Styles
import './styles/index.css'
// deploy test

if (import.meta.env.PROD) {
  Clarity.init('wnz5pmwa8r')
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (failureCount >= 0 && import.meta.env.DEV) return false
        if (failureCount > 3 && import.meta.env.PROD) return false

        return !(
          error instanceof AxiosError &&
          [401, 403].includes(error.response?.status ?? 0)
        )
      },
      refetchOnWindowFocus: import.meta.env.PROD,
      staleTime: 10 * 1000, // 10s
    },
    mutations: {
      onError: (error) => {
        handleServerError(error)

        if (error instanceof AxiosError) {
          if (error.response?.status === 304) {
            toast.error('Content not modified!')
          }
        }
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error) => {
      Sentry.captureException(error)
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          toast.error('Session expired!')
          useAuthStore.getState().auth.reset()
          const redirect = `${router.history.location.href}`
          router.navigate({ to: '/sign-in', search: { redirect } })
        }
        if (error.response?.status === 500) {
          toast.error('Internal Server Error!')
          router.navigate({ to: '/500' })
        }
        if (error.response?.status === 403) {
          // router.navigate("/forbidden", { replace: true });
        }
      }
    },
  }),
})

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  // Todo erro que cai na tela "500" passa por aqui. Aba antiga recarrega sozinha;
  // o resto vai para o Sentry (antes a tela de erro não deixava rastro nenhum).
  defaultOnCatch: (error) => {
    if (isStaleChunkError(error) && reloadOnce()) return
    Sentry.captureException(error)
  },
})

// O mesmo erro chega pelos dois caminhos (evento do Vite e tela de erro do router):
// a recarga já em andamento vale para ambos.
let reloading = false
function reloadOnce(): boolean {
  reloading ||= reloadForStaleChunk()
  return reloading
}

// O Vite avisa por este evento quando um arquivo de rota não carrega.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnce()) event.preventDefault()
})

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Render the app
const rootElement = document.getElementById('root')!
if (!rootElement.innerHTML && ehHostDoEnvio(window.location.hostname)) {
  // atestado.zavy.com.br[/<empresa>]: só a página de envio do funcionário — sem login, rotas ou o resto do app.
  const slug = slugDoCaminho(window.location.pathname)
  // Antes de montar: um erro no carregamento já sai marcado como do envio.
  iniciarObservabilidadeEnvio(slug ?? '_')
  ReactDOM.createRoot(rootElement).render(
    <SentryErrorBoundary>
      <StrictMode>
        {/* sem empresa no endereço: endereço único — o domínio do e-mail aponta o cliente */}
        <EnvioColaboradorPage slug={slug ?? '_'} />
      </StrictMode>
    </SentryErrorBoundary>
  )
} else if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <SentryErrorBoundary>
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <FontProvider>
              <DirectionProvider>
                <AuthProvider>
                  <TenantProvider>
                    <RouterProvider router={router} />
                  </TenantProvider>
                </AuthProvider>
              </DirectionProvider>
            </FontProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </StrictMode>
    </SentryErrorBoundary>
  )
}
